// Shared data layer for Emergency Mode.
//
// One ACTIVE emergency per school lives in the top-level `emergencies` collection.
// Per-student accountability is recorded under `emergencies/{id}/checkIns/{studentId}`
// (doc id = studentId, so a student is checked in at most once — taps are idempotent and
// "who accounted for them" is simply the last writer). Ending sets isActive=false; the
// next start makes a fresh emergency, so accountability resets automatically while history
// is preserved per emergency.
//
// Safety-first: any enrolled staff member can start/join/end and check students in/out.

import {
  collection, query, where, getDocs,
  doc, setDoc, deleteDoc, updateDoc, addDoc, writeBatch,
} from "firebase/firestore";
import { firestore } from "./FirebaseConfig";

export const EMERGENCIES = "emergencies";

// The school's currently-active emergency, or null.
export async function findActiveEmergency(schoolId) {
  if (!schoolId) return null;
  const snap = await getDocs(query(
    collection(firestore, EMERGENCIES),
    where("schoolId", "==", schoolId),
    where("isActive", "==", true),
  ));
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}

// Start the school's emergency, or join the existing active one. Returns its id.
// isDrill marks a practice run vs a real event (used only for the recap/history
// labelling — accountability behaves identically either way).
export async function startEmergency(schoolId, user, startedByName, isDrill = false) {
  const existing = await findActiveEmergency(schoolId);
  if (existing) return existing.id;
  const ref = await addDoc(collection(firestore, EMERGENCIES), {
    schoolId,
    startedBy: user.uid,
    startedByName: startedByName || "",
    startedAt: new Date().toISOString(),
    endedAt: null,
    isActive: true,
    isDrill: !!isDrill,
  });
  return ref.id;
}

// Flip a live emergency between drill and real (if it was started as the wrong kind).
export function setEmergencyKind(emergencyId, isDrill) {
  return updateDoc(doc(firestore, EMERGENCIES, emergencyId), { isDrill: !!isDrill });
}

// End an active emergency. Tracking stops; the next start begins clean.
export function endEmergency(emergencyId) {
  return updateDoc(doc(firestore, EMERGENCIES, emergencyId), {
    isActive: false,
    endedAt: new Date().toISOString(),
  });
}

// Past (ended) emergencies for a school, newest first. Single-field query
// (sameSchool authorizes it, no composite index); ended-only + sort client-side.
export async function listEndedEmergencies(schoolId) {
  if (!schoolId) return [];
  const snap = await getDocs(query(
    collection(firestore, EMERGENCIES),
    where("schoolId", "==", schoolId),
  ));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((e) => e.isActive === false)
    .sort((a, b) => (b.startedAt || "").localeCompare(a.startedAt || ""));
}

// All check-in records for one emergency (one-time read, for a recap).
export async function fetchCheckIns(emergencyId) {
  if (!emergencyId) return [];
  const snap = await getDocs(collection(firestore, EMERGENCIES, emergencyId, "checkIns"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Account for a student (idempotent — keyed by student id). Last writer wins.
export function checkInStudent(emergencyId, student, user, teacherName) {
  return setDoc(
    doc(firestore, EMERGENCIES, emergencyId, "checkIns", student.id),
    {
      studentId: student.id,
      studentName: student.studentName || "",
      homeroomClassId: student.homeroomClassId || student.enrolledClasses?.[0] || "",
      schoolId: student.schoolId || "",
      checkedOffByTeacherId: user.uid,
      checkedOffByTeacherName: teacherName || "",
      timestamp: new Date().toISOString(),
    },
  );
}

// A student accounting for THEMSELVES from their own device. Same shape as a teacher
// check-in (so the Emergency board renders it identically) but flagged as self-served
// and attributed to "Self check-in".
export function selfCheckIn(emergencyId, student, user) {
  return setDoc(
    doc(firestore, EMERGENCIES, emergencyId, "checkIns", student.id),
    {
      studentId: student.id,
      studentName: student.studentName || "",
      homeroomClassId: student.homeroomClassId || student.enrolledClasses?.[0] || "",
      schoolId: student.schoolId || "",
      checkedOffByTeacherId: user.uid,
      checkedOffByTeacherName: "Self check-in",
      selfCheckedIn: true,
      timestamp: new Date().toISOString(),
    },
  );
}

// Undo a check-in (student is no longer accounted for).
export function undoCheckIn(emergencyId, studentId) {
  return deleteDoc(doc(firestore, EMERGENCIES, emergencyId, "checkIns", studentId));
}

// Check in several students at once (e.g. a teacher accounts for a whole class).
export function checkInMany(emergencyId, students, user, teacherName) {
  const batch = writeBatch(firestore);
  students.forEach((s) => {
    batch.set(doc(firestore, EMERGENCIES, emergencyId, "checkIns", s.id), {
      studentId: s.id,
      studentName: s.studentName || "",
      homeroomClassId: s.homeroomClassId || s.enrolledClasses?.[0] || "",
      schoolId: s.schoolId || "",
      checkedOffByTeacherId: user.uid,
      checkedOffByTeacherName: teacherName || "",
      timestamp: new Date().toISOString(),
    });
  });
  return batch.commit();
}
